import { api, el, toast } from "./dom";
import { icon } from "./icons";
import { ib } from "./record";

// "Did you know?" (brief 1D-5, mockup v50 hint): a small dismissable card for people in their
// first days, as the server decides (GET /api/hints?screen=). "Got it, don't show again" is per
// person: the card goes once the server has kept it, and the request is sent so that leaving
// the page straight away doesn't cancel it (keepalive). Nothing shows when there's nothing to
// show, or the call fails.

interface Hint {
  id: string;
  key: string;
  text: string;
  ask: string | null;
}

/** A slot that fills with the screen's hints, if any. */
export function hintsFor(screen: string): HTMLElement {
  const slot = el("div", { "data-hints": screen });
  void (async () => {
    const res = await api<{ hints: Hint[] }>(
      "GET",
      `/api/hints?screen=${encodeURIComponent(screen)}`,
    );
    if (!res.ok) return;
    slot.replaceChildren(
      ...res.body.hints.map((h) => {
        const card: HTMLElement = el(
          "div",
          { class: "dyk", role: "note", "data-hint": h.key },
          icon("light-bulb"),
          el(
            "div",
            {},
            el("b", {}, "Did you know?"),
            ` ${h.text}`,
            h.ask ? el("div", { class: "small muted" }, h.ask) : null,
          ),
          ib("x", "Got it, don't show again", () => void dismiss(card, h.id)),
        );
        return card;
      }),
    );
  })();
  return slot;
}

/** Got it: kept on the server first, then the card goes; if it wasn't kept, the card stays. */
async function dismiss(card: HTMLElement, id: string) {
  const button = card.querySelector("button");
  if (button) button.disabled = true;
  card.style.opacity = "0.6";
  const res = await fetch(`/api/hints/${id}/dismiss`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: "{}",
    cache: "no-store",
    // Leaving the page right after Got it still delivers it.
    keepalive: true,
  }).catch(() => null);
  if (res?.ok) {
    card.remove();
    return;
  }
  if (button) button.disabled = false;
  card.style.opacity = "";
  toast("That wasn't saved", "The hint will show again; try Got it once more");
}
